// Form Submissions API Types
import { FormSubmissionSourceType } from '@commenda/prisma';

/**
 * Status enum for form submissions
 */
export enum FormsSubmissionStatus {
  DRAFT = 'DRAFT',
  COMPLETED = 'COMPLETED',
}

/**
 * Form submission interface representing the data structure from the API
 */
export interface IFormSubmission {
  id: string;
  metadata?: Record<string, unknown> | null;
  formTemplateKeyId: string;
  formTemplateId: string;
  formResponse: Record<string, unknown>;
  userId: string;
  status: FormsSubmissionStatus;
  sourceEntityType?: FormSubmissionSourceType | null;
  sourceEntityId?: string | null;
  submittedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A Commenda OS write that did not land while the form was being submitted. */
export interface ICommendaOSWriteFailure {
  entity: string;
  fieldId?: string;
  error?: string;
  /** The backend's stable error code, for the customer-facing copy. The report ignores it. */
  code?: string;
}

/**
 * Request body for creating a new form submission
 */
export interface CreateFormSubmissionDto {
  formTemplateKey: string;
  formTemplateId: string;
  formData: Record<string, unknown>;
  status?: FormsSubmissionStatus;
  metadata?: Record<string, unknown>;
  companyId?: number;
  serviceRequestId?: string;
  /** Polymorphic source linkage (e.g. the ServiceRequestTask being filled). Both must be set together. */
  sourceEntityType?: FormSubmissionSourceType;
  sourceEntityId?: string;
  commendaOSFailures?: ICommendaOSWriteFailure[];
}

/**
 * Request body for updating a form submission
 */
export interface UpdateFormSubmissionDto {
  formData?: Record<string, unknown>;
  status: FormsSubmissionStatus;
  metadata?: Record<string, unknown>;
  commendaOSFailures?: ICommendaOSWriteFailure[];
}

/**
 * Query parameters for listing form submissions
 */
export interface GetFormSubmissionsParams {
  status?: FormsSubmissionStatus[];
  formTemplateKey?: string[];
  formTemplateId?: string[];
  userId?: string;
  limit?: number;
  offset?: number;
  companyId?: number;
}

/**
 * Response for listing form submissions
 */
export interface FormSubmissionsListResponse {
  formSubmissions: IFormSubmission[];
  count: number;
}

/**
 * Response for getting a single form submission
 */
export interface FormSubmissionResponse {
  formSubmission: IFormSubmission;
}

/**
 * Response for creating a form submission
 */
export interface CreateFormSubmissionResponse {
  formSubmission: IFormSubmission;
}
